import { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Link,
  Stack,
  SvgIcon,
} from "@mui/material";
import { useParams } from "react-router-dom";
import {
  CustomButton,
  CustomFormControl,
  CustomTextField,
  CustomTypography,
} from "src/components/inputs";
import { Seo } from "src/components/seo";
import { withSuppliersGuard } from "src/hocs/with-role-based-guard";
import { paths } from "src/paths";
// Icons
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { RouterLink } from "src/components/router-link";
// Redux
import { useDispatch, useSelector } from "src/store";
import { thunks } from "src/thunks/suppliers";
import { slice } from "src/slices/suppliers";
import moment from "moment";
import toast from "react-hot-toast";

const SupplierDetails = () => {
  const params = useParams();
  const dispatch = useDispatch();

  const supplier_id = params.supplier_id;

  const {
    supplierDetails,
    supplierDetailsLoading,
    submitPortalAccessLoading,
    submitPortalAccessError,
  } = useSelector((state) => state.suppliers);

  const [internalId, setInternalId] = useState("");
  const [supplierCode, setSupplierCode] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [subsidiaryEdition, setSubsidiaryEdition] = useState("");
  const [createdDate, setCreatedDate] = useState("");
  const [lastModifiedDate, setLastModifiedDate] = useState("");
  const [isInactive, setIsInactive] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [duplicate, setDuplicate] = useState(false);
  const [currency, setCurrency] = useState("");
  const [brands, setBrands] = useState([]);
  const [email, setEmail] = useState("");
  const [portalAccess, setPortalAccess] = useState(false);

  // Confirmation dialog states
  const [grantAccessDialogOpen, setGrantAccessDialogOpen] = useState(false);
  const [removeAccessDialogOpen, setRemoveAccessDialogOpen] = useState(false);

  useEffect(() => {
    dispatch(slice.actions.setSubmitPortalAccessError(false));
    dispatch(thunks.getSupplierDetails(supplier_id));
  }, []);

  useEffect(() => {
    if (supplierDetails) {
      setInternalId(supplierDetails?.internalId || "");
      setSupplierCode(supplierDetails?.supplierCode || "");
      setCompanyName(supplierDetails?.companyName || "");
      setLegalName(supplierDetails?.legalName || "");
      setSubsidiaryEdition(supplierDetails?.subsidiaryEdition || "");
      setIsInactive(supplierDetails?.isInactive || false);
      setHasPassword(supplierDetails?.hasPassword || false);
      setSupplierId(supplierDetails?.supplierId || "");
      setDuplicate(supplierDetails?.duplicate || false);
      setCurrency(supplierDetails?.currency || "");
      setBrands(supplierDetails?.brands || []);
      setEmail(supplierDetails?.email || "");
      setPortalAccess(supplierDetails?.portalAccess || false);
      setCreatedDate(
        moment(supplierDetails?.createdDate).format("DD/MM/YYYY") || ""
      );
      setLastModifiedDate(
        moment(supplierDetails?.lastModifiedDate).format("DD/MM/YYYY") || ""
      );
    }
  }, [supplierDetails]);

  //
  // Components
  //

  const renderBackToSuppliersButton = () => {
    return (
      <Container maxWidth={false} style={{ textAlign: "left" }}>
        <Stack spacing={4}>
          <Stack spacing={4}>
            <div>
              <Link
                color="text.primary"
                component={RouterLink}
                href={paths.dashboard.suppliers.index}
                sx={{
                  alignItems: "center",
                  display: "inline-flex",
                }}
                underline="hover"
              >
                <SvgIcon sx={{ mr: 1 }}>
                  <KeyboardBackspaceIcon />
                </SvgIcon>
                <CustomTypography variant="subtitle2" message="Suppliers" />
              </Link>
            </div>
          </Stack>
        </Stack>
      </Container>
    );
  };

  const renderCircularProgress = () => {
    return (
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1100,
          color: "black",
        }}
      >
        <CircularProgress color="inherit" size={35} />
      </Box>
    );
  };

  const handlePortalAccess = () => {
    if (portalAccess) {
      setRemoveAccessDialogOpen(true);
    } else {
      setGrantAccessDialogOpen(true);
    }
  };

  const handleConfirmGrantAccess = async () => {
    setGrantAccessDialogOpen(false);
    const success = await dispatch(thunks.alterPortalAccess(supplier_id));

    if (success) {
      toast.success("Portal access granted");
    }
  };

  const handleConfirmRemoveAccess = async () => {
    setRemoveAccessDialogOpen(false);
    const success = await dispatch(thunks.alterPortalAccess(supplier_id));

    if (success) {
      toast.success("Portal access removed");
    }
  };

  const handleCancelGrantAccess = () => {
    setGrantAccessDialogOpen(false);
  };

  const handleCancelRemoveAccess = () => {
    setRemoveAccessDialogOpen(false);
  };

  const renderGrantPortalAccessButton = () => {
    return (
      <Stack
        style={{
          background: "transparent",
          width: "100%",
          flexDirection: "row",
          gap: 10,
          justifyContent: "center",
          zIndex: 500,
          padding: "25px",
        }}
      >
        <CustomButton
          style={{
            background: "white",
            width: "275px",
          }}
          redButton={submitPortalAccessError}
          label="Grant Portal Access"
          onClick={handlePortalAccess}
          loader={
            submitPortalAccessLoading && (
              <CircularProgress
                size={20}
                color="inherit"
                style={{ marginLeft: "10px" }}
              />
            )
          }
          disabled={submitPortalAccessLoading}
        />
      </Stack>
    );
  };

  const renderRemovePortalAccessButton = () => {
    return (
      <Stack
        style={{
          background: "transparent",
          width: "100%",
          flexDirection: "row",
          gap: 10,
          justifyContent: "center",
          zIndex: 500,
          padding: "25px",
        }}
      >
        <CustomButton
          style={{
            background: "white",
            width: "275px",
          }}
          redButton={submitPortalAccessError}
          label="Remove Portal Access"
          onClick={handlePortalAccess}
          loader={
            submitPortalAccessLoading && (
              <CircularProgress
                size={20}
                color="inherit"
                style={{ marginLeft: "10px" }}
              />
            )
          }
          disabled={submitPortalAccessLoading}
        />
      </Stack>
    );
  };

  const renderGrantAccessConfirmationDialog = () => {
    return (
      <Dialog
        open={grantAccessDialogOpen}
        onClose={handleCancelGrantAccess}
        aria-labelledby="grant-access-dialog-title"
        aria-describedby="grant-access-dialog-description"
        BackdropProps={{
          style: {
            backgroundColor: "transparent",
            backdropFilter: "blur(5px)",
          },
        }}
        PaperProps={{
          style: {
            borderRadius: "25px",
            border: "1px solid #E6E6E6",
            minWidth: "500px",
          },
        }}
      >
        <Stack
          style={{
            height: "40px",
            background: "green",
            borderRadius: "25px 25px 0 0",
          }}
        />
        <DialogContent style={{ padding: "24px" }}>
          <Stack
            id="grant-access-dialog-description"
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 2,
              fontFamily: "tmwCustomFont",
            }}
          >
            Are you sure you want to grant portal access to this supplier?
          </Stack>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px 24px 24px", gap: 5 }}>
          <CustomButton
            label="Cancel"
            smallButton
            onClick={handleCancelGrantAccess}
            style={{ background: "white", border: "1px solid #E6E6E6" }}
          />
          <CustomButton
            label="Grant Access"
            onClick={handleConfirmGrantAccess}
            greenButton
            smallButton
            style={{ width: "140px" }}
            loader={
              submitPortalAccessLoading && (
                <CircularProgress
                  size={20}
                  color="inherit"
                  style={{ marginLeft: "10px" }}
                />
              )
            }
            disabled={submitPortalAccessLoading}
          />
        </DialogActions>
      </Dialog>
    );
  };

  const renderRemoveAccessConfirmationDialog = () => {
    return (
      <Dialog
        open={removeAccessDialogOpen}
        onClose={handleCancelRemoveAccess}
        aria-labelledby="remove-access-dialog-title"
        aria-describedby="remove-access-dialog-description"
        BackdropProps={{
          style: {
            backgroundColor: "transparent",
            backdropFilter: "blur(5px)",
          },
        }}
        PaperProps={{
          style: {
            borderRadius: "25px",
            border: "1px solid #E6E6E6",
            minWidth: "400px",
          },
        }}
      >
        <Stack
          style={{
            height: "40px",
            background: "red",
            borderRadius: "25px 25px 0 0",
          }}
        />
        <DialogContent style={{ padding: "24px" }}>
          <Stack
            id="grant-access-dialog-description"
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 2,
              fontFamily: "tmwCustomFont",
            }}
          >
            Are you sure you want to remove portal access from this supplier?
          </Stack>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px 24px 24px", gap: 5 }}>
          <CustomButton
            label="Cancel"
            onClick={handleCancelRemoveAccess}
            smallButton
            style={{ background: "white", border: "1px solid #E6E6E6" }}
          />
          <CustomButton
            label="Remove Access"
            onClick={handleConfirmRemoveAccess}
            redButton
            smallButton
            style={{ width: "140px" }}
            loader={
              submitPortalAccessLoading && (
                <CircularProgress
                  size={20}
                  color="inherit"
                  style={{ marginLeft: "10px" }}
                />
              )
            }
            disabled={submitPortalAccessLoading}
          />
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <>
      <Seo title="Dashboard: Supplier Details" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 3,
          px: 5,
          mb: 5,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {renderBackToSuppliersButton()}
        <CustomTypography
          message="Supplier Details"
          title
          style={{ textAlign: "center", marginBottom: "40px" }}
        />
        <Stack
          style={{
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!supplierDetailsLoading ? (
            <Stack
              style={{
                flexDirection: "row",
                gap: 135,
                padding: 35,
                border: "1px solid #E6E6E6",
                width: "fit-content",
              }}
            >
              <Stack style={{ gap: 35 }}>
                <CustomTextField
                  style={{ width: "300px" }}
                  label="Internal Id"
                  value={internalId}
                  disabled
                />
                <CustomTextField
                  style={{ width: "300px" }}
                  label="Currency"
                  value={currency}
                  disabled
                />
                <CustomTextField
                  style={{ width: "300px" }}
                  label="Company Name"
                  value={companyName}
                  disabled
                />
                <CustomTextField
                  style={{ width: "300px" }}
                  label="Created Date"
                  value={createdDate}
                  disabled
                />
                <CustomFormControl
                  style={{ margin: "13px 0" }}
                  control={<Checkbox style={{ color: "grey" }} />}
                  label={
                    <CustomTypography
                      style={{ userSelect: "none" }}
                      message="Has Password"
                    />
                  }
                  checked={hasPassword}
                  disabled
                />
              </Stack>
              <Stack style={{ gap: 35 }}>
                <CustomTextField
                  style={{ width: "300px" }}
                  label="Supplier Id"
                  value={supplierId}
                  disabled
                />
                <CustomTextField
                  style={{ width: "300px" }}
                  label="Legal Name"
                  value={legalName}
                  disabled
                />
                <CustomTextField
                  style={{ width: "300px" }}
                  label="Subsidiary Edition"
                  value={subsidiaryEdition}
                  disabled
                />
                <CustomTextField
                  style={{ width: "300px" }}
                  label="Last Modified Date"
                  value={lastModifiedDate}
                  disabled
                />
                <CustomFormControl
                  style={{ margin: "13px 0" }}
                  control={<Checkbox style={{ color: "grey" }} />}
                  label={
                    <CustomTypography
                      style={{ userSelect: "none" }}
                      message="Is Inactive"
                    />
                  }
                  checked={isInactive}
                  disabled
                />
              </Stack>
              <Stack style={{ gap: 35 }}>
                <CustomTextField
                  style={{ width: "300px" }}
                  label="Supplier Code"
                  value={supplierCode}
                  disabled
                />
                <CustomTextField
                  style={{ width: "300px" }}
                  label="Brands"
                  value={Array.isArray(brands) ? brands.join(", ") : brands}
                  disabled
                />
                <CustomTextField
                  style={{ width: "300px" }}
                  label="Email"
                  value={email}
                  disabled
                />
                <CustomFormControl
                  style={{ margin: "13px 0" }}
                  control={<Checkbox style={{ color: "grey" }} />}
                  label={
                    <CustomTypography
                      style={{ userSelect: "none" }}
                      message="Portal Access"
                    />
                  }
                  checked={portalAccess}
                  disabled
                />
                <CustomFormControl
                  style={{ margin: "13px 0" }}
                  control={<Checkbox style={{ color: "grey" }} />}
                  label={
                    <CustomTypography
                      style={{ userSelect: "none" }}
                      message="Duplicate"
                    />
                  }
                  checked={duplicate}
                  disabled
                />
              </Stack>
              {/* move buttons out to place them under the details box */}
            </Stack>
          ) : (
            renderCircularProgress()
          )}
        </Stack>

        {/* Action button directly under the details box */}
        <Stack style={{ alignItems: "center", justifyContent: "center" }}>
          {!portalAccess
            ? renderGrantPortalAccessButton()
            : renderRemovePortalAccessButton()}
        </Stack>

        {/* Confirmation Dialogs */}
        {renderGrantAccessConfirmationDialog()}
        {renderRemoveAccessConfirmationDialog()}
      </Box>
    </>
  );
};

export default withSuppliersGuard(SupplierDetails);
